import { Button, Flex, InputNumber, Space, Switch, Tooltip, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
    avaArea,
    canvasNoticeState,
    deleteObstacleIndex,
    deletePanelIndex,
    panelSettings,
    polygonDetailsSelector,
    polygonsState,
    selectedIndicesState,
    setBackInput
} from "../ThreeJs/ReactStates/states";
import { themeState } from "../lib/atom";
import deleteButtonIcon from "../assets/ui_revamp/DeleteButton.svg";

import { DrawingModes, ObstacleTypes, POLYGONS, VARS } from "../ThreeJs/Global/variables";
import { OBSTACLE } from "../ThreeJs/Global/materials";
import { updateAvaArea, updateSetbackUI } from "../ThreeJs/Segments/SetBacks/Functions";
import { updatePolygonDetailsSelector, updatePolygonSetbacksSelector } from '../ThreeJs/ReactStates/updates';
import { indexToLabel } from '../ThreeJs/Segments/Labels';
import SegmentToggle from "./SegmentToggle";
import SolarPanelSettings from "./SolarPanelSettings";
import { MdCheckBoxOutlineBlank, MdClose, MdOutlineCircle, MdOutlineGridOn } from "react-icons/md";
import { BsPentagon } from "react-icons/bs";
import { selectObstacle } from "../ThreeJs/Obstacles/ObstacleFunctions";
import { changeObstacleFill, changeObstacleHeight, changeObstacleOffset } from "../ThreeJs/Obstacles/Modifications";
import { removeSegmentDragControls, setSegmentDragControls } from "../ThreeJs/Segments/DragControls";
import { disableObstacleVertexDrag } from "../ThreeJs/Obstacles/ObstacleDragControls";
import { COLORS } from "../ThreeJs/Global/materials";
import { pushUndoAction } from "../ThreeJs/Global/UndoManager";
import { updateWireframe } from "../ThreeJs/Segments/SetBacks/Setbacks";
import { sceneManager } from "../ThreeJs/SetUps/SceneManager";
import { setObstacleVisualColor } from "../ThreeJs/Obstacles/Modifications";
import { refreshSegmentPanelOverlapHighlights } from "../ThreeJs/Panels/ValidationVisuals";

const SegmentDetail = ({
    updateObstacleType,
    updateDrawingMode,
    currentDrawingMode,
    setDeleteConfirmationVisible,
    panelList,
}) => {
    // Recoil state and selectors to access current theme, selected segment indices, polygon details, and other states.
    const theme = useRecoilState(themeState)[0];
    const details = useRecoilValue(polygonDetailsSelector);  // pitch, azimuth, area
    const [selectedIndices, setSelectedIndices] = useRecoilState(selectedIndicesState);  // Selected segment index
    const [inputValues, setInputValues] = useRecoilState(setBackInput);  // setback inputs
    const addSetback = useSetRecoilState(updatePolygonSetbacksSelector);
    const setPolygonDetails = useSetRecoilState(updatePolygonDetailsSelector);
    const setPolygons = useSetRecoilState(polygonsState);
    const [, setAvailableArea] = useRecoilState(avaArea);  // setback area for selected segment
    const polygons = useRecoilValue(polygonsState);
    const [showPanelSettings, setShowPanelSettings] = useRecoilState(panelSettings);
    const setDeleteObstacle = useSetRecoilState(deleteObstacleIndex);
    const setDeletePanels = useSetRecoilState(deletePanelIndex);
    const setCanvasNotice = useSetRecoilState(canvasNoticeState);

    const [showObsTab, setShowObsTab] = useState(false);
    const [irradianceBusy, setIrradianceBusy] = useState(
        typeof window !== "undefined" && window.isIrradianceBusy === true
    );
    const addObstacleCardRef = useRef(null);
    const [drawingObstacleType, setDrawingObstacleType] = useState(null);
    const obstacleDrawSessionRef = useRef(null);

    const [activeTab, setActiveTab] = useState("details");
    const PANEL_M2_TO_SQFT = 10.7639;

    const getPanelAreaSqft = (panel) => {
        if (panel?.panel?.geometry?.parameters) {
            const { width, height } = panel.panel.geometry.parameters; // meters
            if (typeof width === "number" && typeof height === "number") {
                return width * height * PANEL_M2_TO_SQFT;
            }
        }

        if (typeof panel?.area === "number") {
            // Heuristic: small = m2, large = ft2 (for compatibility with existing code)
            if (panel.area < 30) {
                return panel.area * PANEL_M2_TO_SQFT;
            }
            return panel.area;
        }

        return 0;
    };

    const segmentIndex = selectedIndices.segmentIndex;
    const segmentData = segmentIndex !== null && segmentIndex !== undefined
        ? (POLYGONS[segmentIndex] || polygons[segmentIndex])
        : null;
    const segmentUi = segmentIndex !== null && segmentIndex !== undefined
        ? polygons[segmentIndex]
        : null;
    const obstacles = segmentData?.obstructions || segmentUi?.obstructions || [];
    const panels = segmentData?.panels || segmentUi?.panels || [];
    const hasObstacles = obstacles.length > 0;
    const hasPanels = panels.length > 0;
    const isSegmentSelected = segmentIndex !== null && segmentIndex !== undefined;

    // ---------- INIT SETBACK INPUTS ON SEGMENT CHANGE ----------
    useEffect(() => {
        if (selectedIndices.segmentIndex !== null) {
            const seg = POLYGONS[selectedIndices.segmentIndex];
            if (!seg || !seg.setBacks || !seg.setBacks.edgesData) return;

            const initialInputValues = {};
            seg.setBacks.edgesData.forEach((edge, index) => {
                initialInputValues[index] = edge.value;
            });
            setInputValues(initialInputValues);
        }
    }, [selectedIndices.segmentIndex]);

    useEffect(() => {
        if (selectedIndices.segmentIndex !== null) {
            setActiveTab("details");
        }
    }, [selectedIndices.segmentIndex]);

    useEffect(() => {
        if (selectedIndices.obstacleIndex !== null) {
            setActiveTab("obstacles");
        }
    }, [selectedIndices.obstacleIndex]);

    useEffect(() => {
        if (Array.isArray(selectedIndices.panelIndex) && selectedIndices.panelIndex.length > 0) {
            setActiveTab("panels");
        }
    }, [selectedIndices.panelIndex]);

    useEffect(() => {
        const hasSelectedSegment =
            selectedIndices.segmentIndex !== null && selectedIndices.segmentIndex !== undefined;
        const hasSelectedObstacle = selectedIndices.obstacleIndex !== null;
        const hasSelectedPanels =
            Array.isArray(selectedIndices.panelIndex) && selectedIndices.panelIndex.length > 0;

        if (!hasSelectedSegment) return;
        if (hasSelectedObstacle || hasSelectedPanels || showPanelSettings) return;

        setActiveTab((prev) => (prev === "details" ? prev : "details"));
    }, [selectedIndices, showPanelSettings]);

    useEffect(() => {
        const nextValue = activeTab === "panels";
        if (showPanelSettings !== nextValue) {
            setShowPanelSettings(nextValue);
        }
    }, [activeTab, showPanelSettings, setShowPanelSettings]);

    useEffect(() => {
        const segmentIndex = selectedIndices.segmentIndex;
        if (segmentIndex === null || segmentIndex === undefined) {
            removeSegmentDragControls();
            return;
        }

        if (irradianceBusy) {
            removeSegmentDragControls();
            disableObstacleVertexDrag();
            return;
        }

        const canEditSegment = activeTab === "details" && selectedIndices.obstacleIndex === null;
        if (canEditSegment) {
            setSegmentDragControls(segmentIndex, setPolygonDetails);
        } else {
            removeSegmentDragControls();
        }
    }, [activeTab, selectedIndices.segmentIndex, selectedIndices.obstacleIndex, setPolygonDetails, irradianceBusy]);

    useEffect(() => {
        const onBusyChange = (event) => {
            setIrradianceBusy(event?.detail?.busy === true);
        };
        window.addEventListener("IRRADIANCE_BUSY_CHANGED", onBusyChange);
        return () => window.removeEventListener("IRRADIANCE_BUSY_CHANGED", onBusyChange);
    }, []);

    useEffect(() => {
        if (activeTab !== "obstacles" && showObsTab) {
            setShowObsTab(false);
            if (currentDrawingMode === DrawingModes.OBSTACLE) {
                updateDrawingMode(DrawingModes.NONE);
            }
        }
    }, [activeTab, showObsTab, currentDrawingMode, updateDrawingMode]);

    useEffect(() => {
        if (!showObsTab) return;

        const handleOutsideClick = (event) => {
            if (!addObstacleCardRef.current) return;
            if (addObstacleCardRef.current.contains(event.target)) return;

            const renderRoot =
                typeof document !== "undefined" ? document.getElementById("canvas") : null;
            const clickedInRender =
                renderRoot && (renderRoot === event.target || renderRoot.contains(event.target));

            // Keep obstacle drawing active when the user clicks the model/canvas.
            if (!clickedInRender) {
                setShowObsTab(false);
                if (currentDrawingMode === DrawingModes.OBSTACLE) {
                    updateDrawingMode(DrawingModes.NONE);
                }
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [showObsTab, currentDrawingMode, updateDrawingMode]);

    useEffect(() => {
        if (currentDrawingMode !== DrawingModes.OBSTACLE && drawingObstacleType !== null) {
            setDrawingObstacleType(null);
        }
    }, [currentDrawingMode, drawingObstacleType]);

    useEffect(() => {
        if (currentDrawingMode === DrawingModes.OBSTACLE) {
            obstacleDrawSessionRef.current = { startCount: obstacles.length };
            return;
        }

        const session = obstacleDrawSessionRef.current;
        if (!session) return;

        if (obstacles.length > session.startCount) {
            setShowObsTab(false);
        }

        obstacleDrawSessionRef.current = null;
    }, [currentDrawingMode, obstacles.length, setShowObsTab]);

    useEffect(() => {
        if (currentDrawingMode !== DrawingModes.OBSTACLE) return;

        const handleKeyDown = (event) => {
            if (event.key !== "Escape") return;
            event.preventDefault();
            if (typeof VARS?.keyListener?.canceldrawing === "function") {
                VARS.keyListener.canceldrawing();
            }
            if (OBSTACLE?.box) OBSTACLE.box.visible = false;
            if (OBSTACLE?.circle) OBSTACLE.circle.visible = false;
            updateDrawingMode(DrawingModes.NONE);
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [currentDrawingMode, updateDrawingMode]);

    const cloneInputState = (valueMap = {}) =>
        Object.entries(valueMap).reduce((acc, [key, val]) => {
            acc[key] = Number(val) || 0;
            return acc;
        }, {});

    const cloneSetbackEdges = (edges = []) =>
        edges.map((edge) => ({
            vertexId1: edge.vertexId1,
            vertexId2: edge.vertexId2,
            value: Number(edge.value) || 0,
            point1: edge.point1.clone(),
            point2: edge.point2.clone(),
        }));

    const buildSetbackSnapshot = (segmentIndexToSnapshot, valueMap = {}) => {
        const segment = POLYGONS?.[segmentIndexToSnapshot];
        const edges = segment?.setBacks?.edgesData || [];
        const uiValues = cloneInputState(valueMap);

        edges.forEach((_edge, edgeIdx) => {
            if (!(edgeIdx in uiValues)) {
                uiValues[edgeIdx] = Number(edges[edgeIdx]?.value) || 0;
            }
        });

        return {
            isSet: Boolean(segment?.setBacks?.isSet),
            edgesData: cloneSetbackEdges(edges),
            uiValues,
        };
    };

    const snapshotsEqual = (left, right) => {
        if (!left || !right) return false;
        if (left.isSet !== right.isSet) return false;
        if ((left.edgesData?.length || 0) !== (right.edgesData?.length || 0)) return false;

        const EPSILON = 1e-6;
        for (let i = 0; i < left.edgesData.length; i++) {
            const a = left.edgesData[i];
            const b = right.edgesData[i];
            if (!a || !b) return false;
            if ((Number(a.value) || 0) !== (Number(b.value) || 0)) return false;
            if ((a.vertexId1 ?? null) !== (b.vertexId1 ?? null)) return false;
            if ((a.vertexId2 ?? null) !== (b.vertexId2 ?? null)) return false;
            if (a.point1.distanceTo(b.point1) > EPSILON) return false;
            if (a.point2.distanceTo(b.point2) > EPSILON) return false;
        }

        return true;
    };

    const restoreSetbackSnapshot = (segmentIndexToRestore, snapshot) => {
        const segment = POLYGONS?.[segmentIndexToRestore];
        if (!segment?.setBacks) return;

        segment.setBacks.edgesData = cloneSetbackEdges(snapshot?.edgesData || []);
        segment.setBacks.isSet = Boolean(snapshot?.isSet);

        if (segment.setBacks.isSet) {
            updateWireframe(segment.setBacks.edgesData, segmentIndexToRestore);
        } else {
            updateWireframe(segment.edges || [], segmentIndexToRestore);
            if (segment.setBacks.mesh) {
                sceneManager.scene.remove(segment.setBacks.mesh);
            }
        }

        setInputValues(cloneInputState(snapshot?.uiValues || {}));
        updateAvaArea(segmentIndexToRestore, setAvailableArea);
        refreshSegmentPanelOverlapHighlights(segmentIndexToRestore);
    };

    // ---------- HANDLERS ----------
    // Uniform setback (all edges)
    const applySetbackChange = (nextValue, edgeIndex = undefined) => {
        const index = selectedIndices.segmentIndex;
        if (index === null || index === undefined) return;

        const beforeSnapshot = buildSetbackSnapshot(index, inputValues);

        addSetback({ setbackAdded: true, segmentIndex: index });
        updateSetbackUI(index, setInputValues, Number(nextValue), edgeIndex);
        updateAvaArea(index, setAvailableArea);
        refreshSegmentPanelOverlapHighlights(index);

        const afterSnapshot = buildSetbackSnapshot(index, POLYGONS?.[index]?.setBacks?.edgesData?.reduce((acc, edge, edgeIdx) => {
            acc[edgeIdx] = Number(edge?.value) || 0;
            return acc;
        }, {}));

        if (snapshotsEqual(beforeSnapshot, afterSnapshot)) return;

        pushUndoAction({
            undo: () => restoreSetbackSnapshot(index, beforeSnapshot),
            redo: () => restoreSetbackSnapshot(index, afterSnapshot),
        });
    };

    const handleInputChange = (value) => {
        if (typeof value !== 'number' || Number.isNaN(value)) {
            return;
        }
        const clampedValue = Math.max(0, value);
        applySetbackChange(clampedValue, undefined);
    };

    // Single-edge setback
    const handleSingleInputChange = (value, index) => {
        if (typeof value !== 'number' || Number.isNaN(value)) {
            return;
        }
        const clampedValue = Math.max(0, value);
        applySetbackChange(clampedValue, Number(index));
    };

    const handleAddObstruction = () => {
        if (irradianceBusy) return;
        setShowPanelSettings(false);
        if (currentDrawingMode === DrawingModes.OBSTACLE) {
            setShowObsTab(true);
            return;
        }
        setShowObsTab((prev) => !prev);
    };

    const startObstacleDrawing = (type) => {
        if (irradianceBusy) return;
        setDrawingObstacleType(type);
        updateObstacleType(type);
        updateDrawingMode(DrawingModes.OBSTACLE);
    };

    const handleObstructionClick = (obsIndex) => {
        if (segmentIndex === null || segmentIndex === undefined) return;
        if (
            selectedIndices.segmentIndex === segmentIndex &&
            selectedIndices.obstacleIndex === obsIndex
        ) {
            return;
        }
        selectObstacle(
            segmentIndex,
            selectedIndices,
            obsIndex,
            setSelectedIndices,
            setPolygonDetails
        );
    };

    const stopObstacleRowClick = (event) => {
        event.stopPropagation();
    };

    const handleDeleteObstacle = (obsIndex) => {
        setDeleteObstacle(obsIndex);
        setDeleteConfirmationVisible(true);
    };

    const handleDeletePanels = () => {
        if (segmentIndex === null || segmentIndex === undefined) return;
        const list = segmentUi?.panels || panels;
        setDeletePanels(Array.from({ length: list.length }, (v, i) => i));
        setDeleteConfirmationVisible(true);
    };

    const updateObstacleUi = (obsIndex, patch) => {
        if (segmentIndex === null || segmentIndex === undefined) return;
        setPolygons((oldPolygons) => {
            if (!Array.isArray(oldPolygons)) return oldPolygons;
            if (segmentIndex < 0 || segmentIndex >= oldPolygons.length) return oldPolygons;
            const segment = oldPolygons[segmentIndex];
            if (!segment || !Array.isArray(segment.obstructions)) return oldPolygons;
            if (obsIndex < 0 || obsIndex >= segment.obstructions.length) return oldPolygons;

            const updated = oldPolygons.slice();
            const nextSegment = { ...segment };
            nextSegment.obstructions = segment.obstructions.slice();
            nextSegment.obstructions[obsIndex] = {
                ...nextSegment.obstructions[obsIndex],
                ...patch
            };
            updated[segmentIndex] = nextSegment;
            return updated;
        });
    };

    const handleObstacleHeightChange = (obsIndex, value) => {
        if (irradianceBusy) return;
        if (typeof value !== "number" || Number.isNaN(value)) return;
        if (segmentIndex === null || segmentIndex === undefined) return;
        if (selectedIndices.obstacleIndex !== obsIndex) {
            handleObstructionClick(obsIndex);
        }
        changeObstacleHeight(segmentIndex, obsIndex, value);
        updateObstacleUi(obsIndex, { height: value });
    };

    const handleObstacleOffsetChange = (obsIndex, value) => {
        if (irradianceBusy) return;
        if (typeof value !== "number" || Number.isNaN(value)) return;
        if (segmentIndex === null || segmentIndex === undefined) return;
        if (selectedIndices.obstacleIndex !== obsIndex) {
            handleObstructionClick(obsIndex);
        }
        const appliedOffset = changeObstacleOffset(segmentIndex, obsIndex, value);
        updateObstacleUi(obsIndex, {
            offset: typeof appliedOffset === "number" ? appliedOffset : value
        });
    };

    const handleObstacleFillChange = (obsIndex, isFilled) => {
        if (irradianceBusy) return;
        if (segmentIndex === null || segmentIndex === undefined) return;
        if (selectedIndices.obstacleIndex !== obsIndex) {
            handleObstructionClick(obsIndex);
        }
        changeObstacleFill(segmentIndex, obsIndex, isFilled);
        updateObstacleUi(obsIndex, { isFilled: Boolean(isFilled) });
    };

    const obstacleColorToHex = (value) => {
        if (typeof value === "number" && Number.isFinite(value)) {
            return `#${Math.max(0, Math.min(0xffffff, value)).toString(16).padStart(6, "0")}`;
        }
        if (typeof value === "string" && /^#([0-9a-f]{6})$/i.test(value)) {
            return value;
        }
        return "#ff0000";
    };

    const handleObstacleColorChange = (obsIndex, colorHex) => {
        if (irradianceBusy) return;
        if (segmentIndex === null || segmentIndex === undefined) return;
        if (typeof colorHex !== "string" || !/^#([0-9a-f]{6})$/i.test(colorHex)) return;

        const obstacle = POLYGONS?.[segmentIndex]?.obstructions?.[obsIndex];
        if (!obstacle) return;

        const nextColor = parseInt(colorHex.slice(1), 16);
        const prevColor =
            typeof obstacle.ogColor === "number" && Number.isFinite(obstacle.ogColor)
                ? obstacle.ogColor
                : COLORS.red;

        if (nextColor === prevColor) return;

        obstacle.ogColor = nextColor;
        if (obstacle.mesh) {
            setObstacleVisualColor(obstacle.mesh, colorHex);
        }

        updateObstacleUi(obsIndex, { ogColor: nextColor });

        pushUndoAction({
            label: "Change obstacle color",
            undo: () => {
                const target = POLYGONS?.[segmentIndex]?.obstructions?.[obsIndex];
                if (!target) return;
                target.ogColor = prevColor;
                if (target.mesh) {
                    setObstacleVisualColor(target.mesh, prevColor);
                }
                updateObstacleUi(obsIndex, { ogColor: prevColor });
            },
            redo: () => {
                const target = POLYGONS?.[segmentIndex]?.obstructions?.[obsIndex];
                if (!target) return;
                target.ogColor = nextColor;
                if (target.mesh) {
                    setObstacleVisualColor(target.mesh, nextColor);
                }
                updateObstacleUi(obsIndex, { ogColor: nextColor });
            },
        });
    };

    // ---------- PREPARE SETBACK INPUT UI ----------
    const stopCollapseToggle = (e) => {
        e.stopPropagation();
    };

    const setbacks = [];
    for (let [index, value] of Object.entries(inputValues)) {
        const label = indexToLabel(index).toLowerCase();
        setbacks.push(
            <Flex className="segment-setback-item" key={index} align="center">
                <Typography.Text
                    style={{ display: 'inline-block', width: '20px' }}
                    className={`${theme ? 'text-gray' : 'text-gray-1'}`}
                >
                    <b>{label}</b>
                </Typography.Text>

                <div
                    onMouseDown={stopCollapseToggle}
                    onClick={stopCollapseToggle}
                >
                    <InputNumber
                        size='small'
                        min={0}
                        step={0.5}
                        style={{ borderColor: theme ? '#555' : '#ccc' }}
                        className={`segment-setback-input ${theme ? 'text-gray' : 'text-gray-1'}`}
                        onChange={(nextValue) => handleSingleInputChange(nextValue, index)}
                        value={value}
                    />
                </div>

            </Flex>
        );
    }

    // Total segment area (sqft)
    const totalAreaSqft =
        details?.area !== null && details?.area !== undefined
            ? parseFloat(details.area)
            : 0;

    const panelsCount = panels.length;
    const totalPanelsAreaSqft = panels.reduce(
        (sum, panel) => sum + getPanelAreaSqft(panel),
        0
    );
    const coveragePct =
        totalAreaSqft > 0
            ? Math.min(100, (totalPanelsAreaSqft / totalAreaSqft) * 100)
            : null;
    const availablePct =
        coveragePct !== null ? Math.max(0, 100 - coveragePct) : null;

    const isObstacleDrawing = currentDrawingMode === DrawingModes.OBSTACLE;
    const showShapePicker = showObsTab;

    const obstacleTypeLabel =
        drawingObstacleType === ObstacleTypes.CIRCLE
            ? "Circle"
            : drawingObstacleType === ObstacleTypes.RECTANGLE
            ? "Rectangle"
            : drawingObstacleType === ObstacleTypes.POLYGON
            ? "Polygon"
            : "Obstacle";

    const segmentLabelValue =
        segmentIndex !== null && segmentIndex !== undefined
            ? (typeof segmentUi?.label === "string" && segmentUi.label) ||
              (typeof segmentData?.label === "string" && segmentData.label) ||
              indexToLabel(segmentIndex)
            : "";

    useEffect(() => {
        const shouldShowNotice = showObsTab && isObstacleDrawing && segmentLabelValue !== "";

        if (shouldShowNotice) {
            setCanvasNotice({
                visible: true,
                intent: "info",
                source: "obstacle-drawing",
                cancelable: true,
                title: `Drawing a ${obstacleTypeLabel} obstacle for section ${segmentLabelValue}`,
                subtitle: "Press Esc to cancel.",
            });
            return;
        }

        setCanvasNotice((prev) => {
            if (prev?.source !== "obstacle-drawing") return prev;
            if (!prev.visible) return prev;
            return { ...prev, visible: false, title: "", subtitle: "", source: null };
        });
    }, [
        showObsTab,
        isObstacleDrawing,
        obstacleTypeLabel,
        segmentLabelValue,
        setCanvasNotice,
    ]);

    if (segmentIndex === null || segmentIndex === undefined) {
        return null;
    }

    const safePanelList = Array.isArray(panelList) ? panelList : [];

    const infoContent = (
        <div
            className={`segment-detail-card segment-detail-card-summary ui-card ui-card--surface-2 ${
                theme ? "dark" : ""
            }`}
        >
            <Flex
                justify='space-between'
                align='center'
                gap= {24}
                style={{ padding: '12px 14px' }}
                className="segment-summary-grid"
            >
                <Space.Compact direction='vertical' className="segment-summary-col">
                    <Typography.Text className="segment-summary-label">
                        Pitch
                    </Typography.Text>
                    <Typography.Text className="segment-summary-value">
                        {details?.pitch != null ? `${details.pitch}\u00B0` : 'N/A'}
                    </Typography.Text>
                </Space.Compact>

                <Space.Compact direction='vertical' className="segment-summary-col">
                    <Typography.Text className="segment-summary-label">
                        Azimuth
                    </Typography.Text>
                    <Typography.Text className="segment-summary-value">
                        {details?.azimu != null ? `${details.azimu}\u00B0` : 'N/A'}
                    </Typography.Text>
                </Space.Compact>

                <Space.Compact direction='vertical' className="segment-summary-col">
                    <Tooltip title="Total area covered in Sqft">
                        <Typography.Text className="segment-summary-label">
                            T. Area
                        </Typography.Text>
                    </Tooltip>
                    <Typography.Text className="segment-summary-value">
                        {totalAreaSqft ? `${totalAreaSqft.toFixed(2)}` : 'N/A'}
                    </Typography.Text>
                </Space.Compact>

                <Space.Compact direction='vertical' className="segment-summary-col">
                    <Typography.Text className="segment-summary-label">
                        Panels
                    </Typography.Text>
                    <Typography.Text className="segment-summary-value">
                        {panelsCount}
                    </Typography.Text>
                </Space.Compact>

                <Space.Compact direction='vertical' className="segment-summary-col">
                    <Tooltip title="Total Area covered by the Panels (sqft)">
                        <Typography.Text className="segment-summary-label">
                            C. Area
                        </Typography.Text>
                    </Tooltip>
                    <Tooltip title="Total Area covered by the Panels (sqft)">
                        <Typography.Text className="segment-summary-value">
                            {coveragePct !== null ? `${coveragePct.toFixed(2)}%` : 'N/A'}
                        </Typography.Text>
                    </Tooltip>
                </Space.Compact>

                <Space.Compact direction='vertical' className="segment-summary-col">
                    <Tooltip title="Area available after Panel placement (sqft)">
                        <Typography.Text className="segment-summary-label">
                            A. Area
                        </Typography.Text>
                    </Tooltip>
                    <Tooltip title="Area available after Panel placement (sqft)">
                        <Typography.Text className="segment-summary-value">
                            {availablePct !== null ? `${availablePct.toFixed(2)}%` : 'N/A'}
                        </Typography.Text>
                    </Tooltip>
                </Space.Compact>
            </Flex>
        </div>
    );

    const setbacksContent = (
        <div
            className={`segment-detail-card segment-detail-card-section ui-card ui-card--surface-2 ${
                theme ? "dark" : ""
            }`}
        >
            <div className="segment-setback-header">
                <Flex align="center" gap={8}>
                    <Typography.Text strong>Setbacks (in)</Typography.Text>
                </Flex>

                <div
                    onMouseDown={stopCollapseToggle}
                    onClick={stopCollapseToggle}
                >
                    <InputNumber
                        size='small'
                        min={0}
                        step={0.5}
                        style={{ borderColor: theme ? '#555' : '#ccc' }}
                        className={`segment-setback-input ${theme ? 'text-gray' : 'text-gray-1'}`}
                        onChange={handleInputChange}
                        value={
                            Object.values(inputValues).length > 0 &&
                            Object.values(inputValues).every(
                                val => val === Object.values(inputValues)[0]
                            )
                                ? Object.values(inputValues)[0]
                                : null
                        }
                    />
                </div>
            </div>

            <div
                className={`segment-setback-grid ${
                    Object.keys(inputValues).length <= 4
                        ? 'segment-setback-grid-2'
                        : 'segment-setback-grid-3'
                }`}
            >
                {setbacks}
            </div>
        </div>
    );

    const obstaclesContent = (
        <div className="segment-detail-tab-content">
            {hasObstacles && (
                <div className={`segment-obstacle-list ${theme ? "dark" : ""}`}>
                    <div className="segment-obstacle-table">
                        <div className="segment-obstacle-header">
                            <div>Name</div>
                            <div>Height</div>
                            <div>Offset</div>
                            <div>Color</div>
                            <div>Fill</div>
                            <div aria-hidden="true" />
                        </div>

                        {obstacles.map((obs, obsIndex) => {
                            const isSelected =
                                selectedIndices.obstacleIndex === obsIndex && isSegmentSelected;
                            const obstacleName = `${
                                obs.type.charAt(0).toUpperCase() +
                                obs.type.slice(1).toLowerCase()
                            } ${obsIndex + 1}`;

                            return (
                                <div
                                    key={obsIndex}
                                    className={`segment-obstacle-row ${
                                        isSelected ? "is-selected" : ""
                                    }`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleObstructionClick(obsIndex)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            handleObstructionClick(obsIndex);
                                        }
                                    }}
                                >
                                    <div className="segment-obstacle-name">{obstacleName}</div>
                                    <div
                                        className="segment-obstacle-control"
                                        onMouseDown={stopObstacleRowClick}
                                        onClick={stopObstacleRowClick}
                                    >
                                        <InputNumber
                                            size="small"
                                            min={0}
                                            step={0.1}
                                            value={
                                                typeof obs.height === "number"
                                                    ? obs.height
                                                    : 0
                                            }
                                            onChange={(value) =>
                                                handleObstacleHeightChange(obsIndex, value)
                                            }
                                            className="segment-obstacle-input"
                                            disabled={irradianceBusy}
                                        />
                                    </div>
                                    <div
                                        className="segment-obstacle-control"
                                        onMouseDown={stopObstacleRowClick}
                                        onClick={stopObstacleRowClick}
                                    >
                                        <InputNumber
                                            size="small"
                                            min={0}
                                            step={1}
                                            value={
                                                typeof obs.offset === "number"
                                                    ? obs.offset
                                                    : 0
                                            }
                                            onChange={(value) =>
                                                handleObstacleOffsetChange(obsIndex, value)
                                            }
                                            className="segment-obstacle-input"
                                            disabled={irradianceBusy}
                                        />
                                    </div>
                                    <div
                                        className="segment-obstacle-control"
                                        onMouseDown={stopObstacleRowClick}
                                        onClick={stopObstacleRowClick}
                                    >
                                        <input
                                            type="color"
                                            value={obstacleColorToHex(obs?.ogColor)}
                                            onChange={(event) =>
                                                handleObstacleColorChange(obsIndex, event.target.value)
                                            }
                                            className="segment-obstacle-color-input"
                                            aria-label={`${obstacleName} color`}
                                            disabled={irradianceBusy}
                                        />
                                    </div>
                                    <div
                                        className="segment-obstacle-control"
                                        onMouseDown={stopObstacleRowClick}
                                        onClick={stopObstacleRowClick}
                                    >
                                        <Switch
                                            size="small"
                                            checked={obs?.isFilled === true}
                                            onChange={(value) =>
                                                handleObstacleFillChange(obsIndex, value)
                                            }
                                            disabled={irradianceBusy}
                                        />
                                    </div>
                                    <Button
                                        danger
                                        type={"text"}
                                        disabled={irradianceBusy}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeleteObstacle(obsIndex);
                                        }}
                                        className="segment-obstacle-delete"
                                        icon={
                                            <img
                                                src={deleteButtonIcon}
                                                alt="Delete obstruction"
                                                className="delete-icon-img segment-obstacle-delete-icon"
                                            />
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="segment-add-actions">
                <div
                    ref={addObstacleCardRef}
                    className={`segment-add-action-card ui-card ui-card--dashed ${
                        theme ? "dark" : ""
                    } ${
                        showObsTab ? "is-expanded" : ""
                    }`}
                >
                    {!showObsTab && (
                        <Button
                            type={"text"}
                            className={`segment-add-action-button ${theme ? "dark" : ""}`}
                            disabled={irradianceBusy}
                            onClick={() => handleAddObstruction()}
                        >
                            Add Obstruction
                        </Button>
                    )}

                    {showShapePicker && (
                        <div className="segment-add-action-panel">
                            <div
                                className={`segment-obstacle-picker ${theme ? "dark" : ""}`}
                            >
                                <button
                                    type="button"
                                    className="segment-obstacle-picker-close"
                                    aria-label="Close obstacle menu"
                                    onClick={() => {
                                        setShowObsTab(false);
                                        if (currentDrawingMode === DrawingModes.OBSTACLE) {
                                            updateDrawingMode(DrawingModes.NONE);
                                        }
                                    }}
                                >
                                    <MdClose />
                                </button>
                                <div className="segment-obstacle-title">Draw Obstruction</div>
                                <div className="segment-obstacle-hint">
                                    Choose a shape, then click the model. Polygon closes on first point.
                                </div>
                                <Flex
                                    className="segment-obstacle-icons"
                                    justify="center"
                                >
                                    <div
                                        className={`segment-obstacle-icon ${
                                            drawingObstacleType === ObstacleTypes.RECTANGLE && isObstacleDrawing
                                                ? "is-active"
                                                : ""
                                        }`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() =>
                                            startObstacleDrawing(ObstacleTypes.RECTANGLE)
                                        }
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                startObstacleDrawing(ObstacleTypes.RECTANGLE);
                                            }
                                        }}
                                    >
                                        <MdCheckBoxOutlineBlank className="segment-obstacle-shape-icon" />
                                    </div>
                                    <div
                                        className={`segment-obstacle-icon ${
                                            drawingObstacleType === ObstacleTypes.CIRCLE && isObstacleDrawing
                                                ? "is-active"
                                                : ""
                                        }`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() =>
                                            startObstacleDrawing(ObstacleTypes.CIRCLE)
                                        }
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                startObstacleDrawing(ObstacleTypes.CIRCLE);
                                            }
                                        }}
                                    >
                                        <MdOutlineCircle className="segment-obstacle-shape-icon" />
                                    </div>
                                    <div
                                        className={`segment-obstacle-icon ${
                                            drawingObstacleType === ObstacleTypes.POLYGON && isObstacleDrawing
                                                ? "is-active"
                                                : ""
                                        }`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() =>
                                            startObstacleDrawing(ObstacleTypes.POLYGON)
                                        }
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                startObstacleDrawing(ObstacleTypes.POLYGON);
                                            }
                                        }}
                                    >
                                        <BsPentagon className="segment-obstacle-shape-icon" />
                                    </div>
                                </Flex>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const panelsContent = (
        <div className="segment-detail-tab-content">
            {/*
            {hasPanels && (
                <div className={`segment-section-card ui-card ${theme ? "dark" : ""}`}>
                    <div className="segment-pill-section">
                        <Flex style={{ alignItems: "center", justifyContent: "space-between" }}>
                            <Flex
                                gap="small"
                                className={`${
                                    theme
                                        ? showPanelSettings && isSegmentSelected
                                            ? "text-blue"
                                            : "text-gray fw-500"
                                        : showPanelSettings && isSegmentSelected
                                        ? "text-blue"
                                        : "text-gray-1 fw-500"
                                }`}
                                style={{ marginLeft: "40px" }}
                            >
                                <MdOutlineGridOn style={{ fontSize: "17px" }} />
                                <Typography.Text
                                    style={{ fontSize: "12px", transition: "color 0.3s" }}
                                    className={`${
                                        showPanelSettings && isSegmentSelected
                                            ? "text-blue"
                                            : "text-gray-1"
                                    }`}
                                    onClick={() => setShowPanelSettings(true)}
                                >
                                    Solar Panels
                                </Typography.Text>
                            </Flex>

                            <Button
                                danger
                                type={"text"}
                                onClick={handleDeletePanels}
                                className="delete-button"
                                style={{ marginRight: "25px" }}
                                icon={
                                    <img
                                        src={deleteButtonIcon}
                                        alt="Delete panels"
                                        className="delete-icon-img"
                                    />
                                }
                            />
                        </Flex>
                    </div>
                </div>
            )}
            */}

            <div className="segment-panel-settings-row">
                <SolarPanelSettings
                    updateDrawingMode={updateDrawingMode}
                    currentDrawingMode={currentDrawingMode}
                    setShowPanelSettings={setShowPanelSettings}
                    deleteConfirmation={setDeleteConfirmationVisible}
                    panelList={safePanelList}
                />
            </div>
        </div>
    );

    return (
        <div className="segment-detail">
            <Flex
                vertical
                gap={10}
                style={{ overflowX: 'hidden' }}
                className={`${theme ? 'custom-scrollbar-dark' : 'custom-scrollbar'}`}
            >
                <SegmentToggle
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    theme={theme}
                />

                {activeTab === "details" && infoContent}
                {activeTab === "setbacks" && setbacksContent}
                {activeTab === "obstacles" && obstaclesContent}
                {activeTab === "panels" && panelsContent}
            </Flex>
        </div>
    );
};

export default SegmentDetail;
